"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.SendMailSchema = exports.SendChatMessageSchema = exports.StartConversationSchema = exports.RegisterPushTokenSchema = exports.UpdateClinicConfigSchema = exports.SetAvailabilitySchema = exports.AvailabilityBlockSchema = exports.UpdateAppointmentSchema = exports.CreateAppointmentSchema = exports.CreatePrescriptionSchema = exports.CreateEyeRecordSchema = exports.EyeMeasurementSchema = exports.UpdatePatientSchema = exports.CreatePatientSchema = exports.UpdateUserSchema = exports.CreateUserSchema = exports.RefreshInputSchema = exports.LoginInputSchema = exports.APPOINTMENT_STATUS_LABELS = exports.AppointmentStatusSchema = exports.APPOINTMENT_TYPE_LABELS_HE = exports.APPOINTMENT_TYPE_LABELS = exports.AppointmentTypeSchema = exports.PlatformSchema = exports.ROLE_LABELS = exports.RoleSchema = void 0;
exports.isClinician = isClinician;
const zod_1 = require("zod");
/* ====================================================================
 * Enums
 * ================================================================== */
exports.RoleSchema = zod_1.z.enum(["ADMIN", "DOCTOR", "RECEPTIONIST"]);
exports.ROLE_LABELS = {
    ADMIN: "Admin",
    DOCTOR: "Doctor",
    RECEPTIONIST: "Clinic Staff",
};
/** Roles with clinician-level privileges (admin inherits the doctor's access). */
function isClinician(role) {
    return role === "DOCTOR" || role === "ADMIN";
}
exports.PlatformSchema = zod_1.z.enum(["IOS", "ANDROID", "WEB"]);
exports.AppointmentTypeSchema = zod_1.z.enum([
    "ROUTINE",
    "FOLLOW_UP",
    "LENS_FITTING",
    "URGENT",
    "PHONE_CONSULT",
]);
exports.APPOINTMENT_TYPE_LABELS = {
    ROUTINE: "Routine exam",
    FOLLOW_UP: "Follow-up",
    LENS_FITTING: "Lens fitting",
    URGENT: "Urgent",
    PHONE_CONSULT: "Phone consult",
};
/** Hebrew labels — used in patient-facing emails. */
exports.APPOINTMENT_TYPE_LABELS_HE = {
    ROUTINE: "בדיקה שגרתית",
    FOLLOW_UP: "ביקורת מעקב",
    LENS_FITTING: "התאמת עדשות",
    URGENT: "בדיקה דחופה",
    PHONE_CONSULT: "ייעוץ טלפוני",
};
exports.AppointmentStatusSchema = zod_1.z.enum([
    "SCHEDULED",
    "CONFIRMED",
    "COMPLETED",
    "CANCELLED",
    "NO_SHOW",
]);
exports.APPOINTMENT_STATUS_LABELS = {
    SCHEDULED: "Scheduled",
    CONFIRMED: "Confirmed",
    COMPLETED: "Completed",
    CANCELLED: "Cancelled",
    NO_SHOW: "No-show",
};
/* ====================================================================
 * Auth
 * ================================================================== */
exports.LoginInputSchema = zod_1.z.object({
    email: zod_1.z.string().email("Invalid email address"),
    password: zod_1.z.string().min(8, "Password must be at least 8 characters"),
});
exports.RefreshInputSchema = zod_1.z.object({
    refreshToken: zod_1.z.string().optional(),
});
/* ====================================================================
 * Users (staff management)
 * ================================================================== */
exports.CreateUserSchema = zod_1.z.object({
    email: zod_1.z.string().email(),
    name: zod_1.z.string().min(2),
    role: exports.RoleSchema,
    password: zod_1.z.string().min(8),
});
exports.UpdateUserSchema = exports.CreateUserSchema.partial();
/* ====================================================================
 * Patients
 * ================================================================== */
exports.CreatePatientSchema = zod_1.z.object({
    fullName: zod_1.z.string().min(2, "Full name is required"),
    dob: zod_1.z.string().min(1, "Date of birth is required"),
    idNumber: zod_1.z.string().min(5, "ID number is required"),
    phone: zod_1.z.string().min(6, "Phone is required"),
    email: zod_1.z.string().email().optional().or(zod_1.z.literal("")),
    address: zod_1.z.string().optional(),
    notes: zod_1.z.string().optional(),
});
exports.UpdatePatientSchema = exports.CreatePatientSchema.partial();
/* ====================================================================
 * Eye records
 * ================================================================== */
/**
 * Numeric form field that treats "", null and undefined as "not measured"
 * (plain z.coerce would turn null/"" into 0 — clinically wrong).
 */
const measurement = (min, max, int = false) => zod_1.z
    .preprocess((v) => {
    if (v === "" || v === null || v === undefined)
        return null;
    const n = typeof v === "number" ? v : Number(v);
    return Number.isNaN(n) ? null : n;
}, (int ? zod_1.z.number().int() : zod_1.z.number()).min(min).max(max).nullable())
    .optional();
exports.EyeMeasurementSchema = zod_1.z.object({
    /** Sphere, diopters. e.g. -1.25 */
    sph: measurement(-30, 30),
    /** Cylinder, diopters. */
    cyl: measurement(-15, 15),
    /** Axis, degrees 0–180. */
    axis: measurement(0, 180, true),
    /** Reading addition, diopters. */
    add: measurement(0, 5),
    /** Intraocular pressure, mmHg. */
    iop: measurement(0, 80),
});
exports.CreateEyeRecordSchema = zod_1.z.object({
    visitDate: zod_1.z.string().optional(),
    diagnosis: zod_1.z.string().min(2, "Diagnosis is required"),
    rightEye: exports.EyeMeasurementSchema.default({}),
    leftEye: exports.EyeMeasurementSchema.default({}),
    /** Free-text prescription / treatment plan written at this exam. */
    prescription: zod_1.z.string().optional(),
    notes: zod_1.z.string().optional(),
});
/* ====================================================================
 * Prescriptions
 * ================================================================== */
exports.CreatePrescriptionSchema = zod_1.z.object({
    patientId: zod_1.z.string().min(1),
    eyeRecordId: zod_1.z.string().min(1),
});
/* ====================================================================
 * Appointments
 * ================================================================== */
exports.CreateAppointmentSchema = zod_1.z.object({
    patientId: zod_1.z.string().min(1, "Patient is required"),
    doctorId: zod_1.z.string().min(1, "Doctor is required"),
    startTime: zod_1.z.string().min(1, "Start time is required"),
    endTime: zod_1.z.string().min(1, "End time is required"),
    type: exports.AppointmentTypeSchema,
    notes: zod_1.z.string().optional(),
});
exports.UpdateAppointmentSchema = zod_1.z.object({
    startTime: zod_1.z.string().optional(),
    endTime: zod_1.z.string().optional(),
    type: exports.AppointmentTypeSchema.optional(),
    status: exports.AppointmentStatusSchema.optional(),
    notes: zod_1.z.string().optional(),
});
/* ====================================================================
 * Availability
 * ================================================================== */
exports.AvailabilityBlockSchema = zod_1.z.object({
    /** 0 = Sunday … 6 = Saturday */
    dayOfWeek: zod_1.z.number().int().min(0).max(6),
    /** Minutes since midnight, e.g. 540 = 09:00 */
    startMinute: zod_1.z.number().int().min(0).max(1439),
    endMinute: zod_1.z.number().int().min(1).max(1440),
});
exports.SetAvailabilitySchema = zod_1.z.object({
    blocks: zod_1.z.array(exports.AvailabilityBlockSchema),
});
/* ====================================================================
 * Clinic config
 * ================================================================== */
exports.UpdateClinicConfigSchema = zod_1.z.object({
    name: zod_1.z.string().min(2).optional(),
    address: zod_1.z.string().optional(),
    phone: zod_1.z.string().optional(),
    email: zod_1.z.string().email().optional().or(zod_1.z.literal("")),
    logoUrl: zod_1.z.string().optional(),
});
/* ====================================================================
 * Push notifications
 * ================================================================== */
exports.RegisterPushTokenSchema = zod_1.z.object({
    token: zod_1.z.string().min(1),
    platform: exports.PlatformSchema,
});
/* ====================================================================
 * Internal chat
 * ================================================================== */
exports.StartConversationSchema = zod_1.z.object({
    /** Staff user ids to include (besides the current user). */
    participantIds: zod_1.z.array(zod_1.z.string().min(1)).min(1, "Pick at least one person"),
    /** Optional name; when set (or >1 participant) the conversation is a group. */
    title: zod_1.z.string().trim().max(80).optional(),
});
exports.SendChatMessageSchema = zod_1.z.object({
    body: zod_1.z.string().trim().min(1, "Message cannot be empty").max(4000),
});
/* ====================================================================
 * Internal email
 * ================================================================== */
exports.SendMailSchema = zod_1.z.object({
    recipientIds: zod_1.z.array(zod_1.z.string().min(1)).min(1, "Pick at least one recipient"),
    subject: zod_1.z.string().trim().min(1, "Subject is required").max(160),
    body: zod_1.z.string().trim().min(1, "Message is required").max(20000),
    /** Set when replying, to thread the message. */
    parentId: zod_1.z.string().optional(),
});
//# sourceMappingURL=index.js.map